import type { Auth0Client } from "@auth0/auth0-spa-js";
import { ApiErrorSchema, type ApiRelativePath } from "@toy/contracts";
import type { ZodType } from "zod";

export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
  }
}

export class AdminApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly auth: Auth0Client,
  ) {}

  async get<T>(path: ApiRelativePath, schema: ZodType<T>): Promise<T> {
    return this.request(path, schema, { method: "GET" });
  }

  async post<T>(
    path: ApiRelativePath,
    body: unknown,
    schema: ZodType<T>,
  ): Promise<T> {
    return this.request(path, schema, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Idempotency-Key": crypto.randomUUID() },
    });
  }

  private async request<T>(
    path: ApiRelativePath,
    schema: ZodType<T>,
    init: RequestInit,
  ): Promise<T> {
    const token = await this.auth.getTokenSilently();
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = ApiErrorSchema.safeParse(payload);
      throw new AdminApiError(
        response.status,
        parsed.success ? parsed.data.error.code : "UNKNOWN",
        parsed.success ? parsed.data.error.message : response.statusText,
        parsed.success ? parsed.data.error.requestId : undefined,
      );
    }
    return schema.parse(payload);
  }
}
